import axios, { AxiosRequestConfig, AxiosError } from "axios"; // Import axios and AxiosError
import { GetCookie } from "../../cookies"; // Assuming GetCookie is correctly defined elsewhere
import { SetTextData } from "../../providers/MessageProvider"; // Assuming SetTextData is correctly defined
import isArray from "lodash/isArray";
import isString from "lodash/isString";

type HttpMethod = "get" | "post" | "patch" | "delete" | "GET" | "POST" | "PATCH" | "DELETE";

interface HandleAxiosProps {
  method?: HttpMethod;
  url: string;
  data?: any | null;
  setTextData?: SetTextData | null;
  onComplete: (response: any) => void;
  onError?: (error: string) => void; // Changed to expect a string
  contentType?: string;
  responseType?: "arraybuffer" | "blob" | "document" | "json" | "text" | "stream";
  loading_delay?: number;
  additional_headers?: { [key: string]: string };
  use_external_api?: boolean;
  timeout?: number;
}

interface HandleAxiosReturn {
  abort: () => void;
}


export const HandleAxios = ({
  method = "get",
  url,
  setTextData,
  onComplete,
  onError,
  data = null,
  contentType = "application/json",
  responseType = "json",
  additional_headers = {},
  use_external_api = false,
  timeout = 100000
}: HandleAxiosProps): HandleAxiosReturn => {
  const controller = new AbortController();

  const executeRequest = async () => {
    try {
      let headers: { [key: string]: string } = {
        "Content-Type": contentType,
        "X-CSRFToken": GetCookie("csrfmiddlewaretoken") || "",
        ...additional_headers,
      };

      let full_url = url;
      if (use_external_api) {
        try {
          const apiUrl = GetCookie("manufacturing_api_key") || "";
          const external_url = GetCookie("manufacturing_site_url") || "";
          if (!apiUrl || !external_url) {
            throw new Error("Failed to get external API key or URL from cookies. Please check application configuration.");
          }
          if (!full_url.startsWith("http://") && !full_url.startsWith("https://")) {
            full_url = `${external_url.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;
          }
          headers["X-Api-Key"] = apiUrl;
        } catch (error: any) {
          const setupErrorMessage = `External API setup error: ${error.message}`;
          console.error(setupErrorMessage, error);
          if (onError) {
            onError(setupErrorMessage);
          } else if (setTextData) {
            setTextData({ text: setupErrorMessage, variant: "danger" });
          }
          return; 
        }
      }

      const config: AxiosRequestConfig = {
        url: full_url,
        method,
        headers,
        responseType: responseType,
        timeout: timeout,
        signal: controller.signal,
      };

      if (method.toLowerCase() === "get" || method.toLowerCase() === "delete") {
        config.params = data;
      } else {
        config.data = data;
      }

      const response = await axios.request(config);

      if (response?.data && response.data?.text && setTextData) {
        setTextData({ text: response.data.text, variant: response.data.variant });
      }

      if (onComplete) {
        onComplete(response);
      }
    } catch (error: any) {
      if (axios.isCancel(error)) {
        console.log("HandleAxios: Request canceled by AbortController", error.message);
        if (onError) {
          onError("Request was canceled by the user.");
        }
        return;
      }

      let errorMessage = "An unexpected error occurred. Please try again or contact support.";
      const errorVariant = "danger";

      if (error.code === 'ECONNABORTED') {
        errorMessage = `The request timed out after ${timeout / 1000} seconds. Please check your internet connection and try again. If the problem persists, the server might be temporarily unavailable.`;
      } else if (axios.isAxiosError(error) && error.response) {
        const { status, data: responseData } = error.response;
        let specificStatusMessage = "";

        switch (status) {
            case 400: specificStatusMessage = `Bad Request (Status 400): Details: ${parseBackendErrorToString(responseData, status)}`; break;
            case 401: specificStatusMessage = "Unauthorized (Status 401): Access is denied. You may need to log in or your credentials are not valid for this resource."; break;
            case 403: specificStatusMessage = "Forbidden (Status 403): You do not have permission to access this resource. Contact an administrator if you believe this is an error."; break;
            case 404: specificStatusMessage = `Not Found (Status 404): The requested resource at could not be found on the server.`; break;
            case 429: specificStatusMessage = "Too Many Requests (Status 429): You have sent too many requests in a short period. Please wait a moment and try again."; break;
        }
        
        if (status >= 500) { // Handle 5xx server errors
            specificStatusMessage = `Server Error (Status ${status}): The server encountered an internal problem and could not complete your request. Please try again later. If the issue persists, contact support. Details: ${parseBackendErrorToString(responseData, status)}`;
        }

        if (specificStatusMessage) {
            errorMessage = specificStatusMessage;
        } else {
            // For other status codes not explicitly handled above (e.g., 409, 422), parse the body
            if (responseData instanceof Blob && responseData.type === 'application/json') {
                try {
                    const errorText = await responseData.text();
                    const errorJson = JSON.parse(errorText);
                    errorMessage = parseBackendErrorToString(errorJson, status);
                } catch (parseError: any) {
                    console.error("HandleAxios: Failed to parse JSON error blob:", parseError);
                    errorMessage = `Error (Status ${status}): Failed to parse error information from the server's response.`;
                }
            } else {
                errorMessage = parseBackendErrorToString(responseData, status);
            }
        }
      } else if (axios.isAxiosError(error) && error.request) {
        errorMessage = "Network Error: No response was received from the server. This could be due to a network issue, a misconfigured URL, or the server being down. Please check your connection and the API endpoint.";
      } else {
        errorMessage = `Request Setup Error: ${error.message || "An unknown error occurred while preparing the request. Please check the console for more details."}`;
      }
      
      if (!errorMessage || String(errorMessage).trim() === "") {
           errorMessage = "An unknown error occurred. Please contact support or check the application logs.";
      }
      
      // Log the final error message and the original error object for debugging
      console.error("HandleAxios Error Message:", errorMessage);
      console.error("HandleAxios Original Error Object:", error.response || error.request || error);

      if (onError) {
        onError(errorMessage);
      } else if (setTextData) {
        setTextData({ text: errorMessage, variant: errorVariant });
      }
    }
  };

  executeRequest();

  return {
    abort: () => {
      console.log("HandleAxios: Abort called via returned object.");
      controller.abort();
    },
  };
};

// Helper function to parse various backend error response formats into a single string.
const parseBackendErrorToString = (responseData: any, status: number): string => {
    if (!responseData) {
        return `Server error (Status ${status}): No error data received from the server.`;
    }

    // 1. If responseData is a simple string
    if (isString(responseData)) {
        const trimmedData = responseData.trim();
        if (trimmedData.startsWith("<!DOCTYPE html>")) {
            return `Server error (Status ${status}): Received an HTML page instead of expected data. This often indicates a server-side misconfiguration or error page.`;
        }
        return trimmedData || `Server error (Status ${status}): An empty error message was received.`;
    }

    // 2. If responseData is an array (e.g., array of error messages or objects)
    if (isArray(responseData)) {
        const messages = responseData
            .map((item: any) => {
                if (isString(item)) return item.trim();
                if (typeof item === 'object' && item !== null) {
                    if (isString(item.message)) return item.message.trim();
                    if (isString(item.error)) return item.error.trim();
                    if (isString(item.detail)) return item.detail.trim();
                    // Fallback for simple error objects in array by attempting to stringify
                    try {
                        const itemStr = JSON.stringify(item);
                        if (itemStr !== '{}') return itemStr; // Avoid returning empty object strings
                    } catch { /* ignore serialization errors for individual items */ }
                }
                return null; // Mark non-parseable or irrelevant items for filtering
            })
            .filter(Boolean); // filter(Boolean) removes null, undefined, empty strings

        if (messages.length > 0) return messages.join('; ');
    }

    // 3. If responseData is an object
    if (typeof responseData === 'object' && responseData !== null) {
        // Check for common top-level error fields
        if (isString(responseData.detail)) return responseData.detail.trim();
        if (isString(responseData.message)) return responseData.message.trim();
        if (isString(responseData.error_description)) return responseData.error_description.trim(); // Common for OAuth

        // If responseData.error is a string
        if (isString(responseData.error)) {
             if (responseData.error.trim().startsWith("<!DOCTYPE html>")) {
                return `Server error (Status ${status}): The error field contained an HTML response.`;
            }
            return responseData.error.trim();
        }

        // If responseData.error is an object (e.g., validation errors: { field1: ["msg1"], field2: "msg2" })
        if (typeof responseData.error === 'object' && responseData.error !== null) {
            const errorObj = responseData.error;
            const detailedMessages: string[] = [];
            for (const key in errorObj) {
                if (Object.prototype.hasOwnProperty.call(errorObj, key)) {
                    const value = errorObj[key];
                    if (isArray(value)) {
                        detailedMessages.push(`${key}: ${value.filter(isString).map(s => s.trim()).join(', ')}`);
                    } else if (isString(value)) {
                        detailedMessages.push(`${key}: ${value.trim()}`);
                    }
                }
            }
            if (detailedMessages.length > 0) return detailedMessages.join('; ');
        }
        
        // If responseData itself is an object of errors (e.g., .NET ModelState or similar validation summary)
        // Example: { "field1": ["Error1", "Error2"], "field2": ["Error3"] }
        const fieldErrorMessages: string[] = [];
        let hasRecognizableFields = false; // To check if we actually parsed field errors
        for (const key in responseData) {
            if (Object.prototype.hasOwnProperty.call(responseData, key)) {
                const value = responseData[key];
                // Check if the value is an array of strings (common for field errors)
                if (isArray(value) && value.every(isString)) {
                     fieldErrorMessages.push(`${key}: ${value.map(s => s.trim()).join(', ')}`);
                     hasRecognizableFields = true;
                // Check if the value is a string and the key suggests it's an error field
                } else if (isString(value) && (key.toLowerCase().includes('error') || key.toLowerCase().includes('message'))) {
                     fieldErrorMessages.push(`${key}: ${value.trim()}`);
                     hasRecognizableFields = true;
                }
            }
        }
        // Only return joined fieldErrorMessages if we actually found and parsed some.
        if (fieldErrorMessages.length > 0 && hasRecognizableFields) return fieldErrorMessages.join('; ');

        // Fallback for other object structures: try to stringify if it's not empty
        if (Object.keys(responseData).length > 0) {
            try {
                const prettyPrinted = JSON.stringify(responseData);
                if (prettyPrinted === '{}') return `Server error (Status ${status}): Received an empty JSON object as an error.`;
                // Limit the length of the stringified object to avoid overly long messages
                const maxLength = 250;
                return `Server error (Status ${status}): Received a complex error object. Details: ${prettyPrinted.substring(0, maxLength)}${prettyPrinted.length > maxLength ? '...' : ''}`;
            } catch (e) {
                return `Server error (Status ${status}): Received a non-serializable object as an error.`;
            }
        }
    }

    // Ultimate fallback if no specific message could be parsed
    return `Server error (Status ${status}): An unexpected error format was received. Please check server logs for details.`;
};

